import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  Pencil,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useCachedPaginatedApi } from "../../hooks/useCachedData";
import { API_ENDPOINTS } from "../../services/api/apiConfig";
import Header from "../common/Header";
import LoadingScreen from "../common/LoadingScreen";
import ResponseMessage from "../common/ResponseMessage";

export interface MedicalRecord {
  id: number;
  symptoms: string;
  symptomDuration: string;
  currentMedication: string;
  preexistingCondition: string;
  familyHistory: string;
  allergies: string;
  assessment: string;
  recommendation: string;
  doctorId: number;
  patientId: number | null;
  consultationFee: number;
  amountPaid: number;
  amountDue: number;
  dateCreation: string;
  systemsReview: string | null;
  observations: string | null;
  diagnostic: string | null;
  medicalHistorySummary: string | null;
  lastAnnualCheckupDate: string | null;
  specialtyType: string | null;
  gynecologyInfo: GynecologyInfo | null;
  pediatricsInfo: PediatricsInfo | null;
  additionalExaminations: AdditionalExamination[];
  appointmentId: number | null;
  medicalHistoryId: number | null;
  constanteId: number | null;
  patientFullName?: string;
  patientPhone?: string;
}

interface GynecologyInfo {
  menopauseAge: number | null;
  hysterectomyDate: string | null;
  lastSmearTestDate: string | null;
  lastMammographyDate: string | null;
  pregnancyCount: number | null;
  hadAbnormalSmear: boolean | null;
  currentContraceptiveMethod: string | null;
  pastContraceptiveMethods: string | null;
  lastMenstrualDate: string | null;
  menstrualCycleLength: number | null;
  childrenCount: number | null;
  livingChildrenCount: number | null;
  deceasedChildrenCount: number | null;
  stillbornCount: number | null;
  miscarriageCount: number | null;
  deliveryType: string | null;
}

interface PediatricsInfo {
  livingWith: string | null;
  hasPets: boolean | null;
  householdSmoking: boolean | null;
  childcareSchool: string | null;
  childSmoking: boolean | null;
  prenatalCareLocation: string | null;
  pregnancyComplications: string | null;
  tobaccoOrMedicationDuringPregnancy: string | null;
  birthLocation: string | null;
  birthWeight: number | null;
  gestationalAge: number | null;
  deliveryComplications: string | null;
  deliveryComplicationsDescription: string | null;
}

interface AdditionalExamination {
  examName: string;
  reason: string;
  concernedDiagnostics: string;
  patientAdvice: string;
}

const RecordForm: React.FC = () => {
  const [records, setRecords] = useState<MedicalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { fetchPage, invalidateCache, getCacheStats } = useCachedPaginatedApi(
    API_ENDPOINTS.MEDICAL_RECORDS.LIST,
    { pageSize: recordsPerPage, ttl: 5 * 60 * 1000 } // 5 minutes de cache
  );

  const fetchRecords = useCallback(async (page: number = currentPage) => {
    try {
      setLoading(true);
      setError(null);

      const result = await fetchPage(page);
      
      if (result && result.data) {
        setRecords(result.data);
        setTotalPages(Math.ceil(result.total / recordsPerPage));
      } else {
        throw new Error("Unexpected response structure from API");
      }

      setLoading(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Erreur inconnue lors du chargement des dossiers médicaux";
      setError(errorMessage);
      setLoading(false);
    }
  }, [currentPage, fetchPage, recordsPerPage]);

  const handleRefresh = () => {
    // Invalider le cache et recharger
    invalidateCache();
    fetchRecords(currentPage);
  };

  // Afficher les statistiques du cache en développement
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Medical Records Cache Stats:', getCacheStats());
    }
  }, [getCacheStats]);

  useEffect(() => {
    fetchRecords();
  }, [currentPage, recordsPerPage, refreshTrigger, fetchRecords]);

  // Auto-refresh when window regains focus
  useEffect(() => {
    const handleFocus = () => {
      setRefreshTrigger(prev => prev + 1);
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  const handleViewRecord = (recordId: number) => {
    navigate(`/doctor/dashboard/record/view/${recordId}`);
  };

  const handleUpdateRecord = (recordId: number) => {
    navigate(`/doctor/dashboard/record/edit/${recordId}`);
  };

  const filteredRecords = records.filter(
    (record) =>
      (record.patientFullName?.toLowerCase() || "").includes(
        searchTerm.toLowerCase()
      ) ||
      (record.patientPhone?.toLowerCase() || "").includes(searchTerm.toLowerCase())
  );

  // Pagination is handled by backend now, so we just show the records we have
  const currentRecords = filteredRecords;

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const nextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const firstPage = () => setCurrentPage(1);
  const lastPage = () => setCurrentPage(totalPages);

  if (loading)
    return (
      <LoadingScreen
        message="Chargement des dossiers médicaux..."
        role="doctor"
      />
    );
  if (error) return <ResponseMessage message={error} role="doctor" />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <Header
        title="Dossiers Médicaux"
        subtitle="Gérez vos dossiers médicaux."
        icon={<Plus className="w-6 h-6 text-white" />}
        role="doctor"
        actionLink="/doctor/dashboard/record/new"
        actionText="Nouveau Dossier"
        description=""
        refreshAction={handleRefresh}
        refreshText="Actualiser"
      />
      <div className="px-6">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 animate-slideInUp">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-grow">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Rechercher par patient ou téléphone..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-colors"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Rechercher des dossiers médicaux"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    { label: "Patient", class: "" },
                    { label: "Téléphone", class: "" },
                    { label: "Date", class: "" },
                    { label: "Diagnostic", class: "" },
                    { label: "Actions", class: "text-center" },
                  ].map((header, index) => (
                    <th
                      key={index}
                      scope="col"
                      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${header.class}`}
                    >
                      {header.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentRecords.length > 0 ? (
                  currentRecords.map((record) => (
                    <tr
                      key={record.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                        {record.patientFullName || "Nom inconnu"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {record.patientPhone || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {record.dateCreation
                          ? new Date(record.dateCreation).toLocaleDateString(
                            "fr-FR"
                          )
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500 max-w-xs truncate">
                        {record.diagnostic || record.assessment || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-3">
                          <button
                            className="p-1.5 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-800 transition-colors"
                            onClick={() => handleViewRecord(record.id)}
                            aria-label="Voir le dossier"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            className="p-1.5 rounded-full bg-yellow-50 text-yellow-500 hover:bg-yellow-100 hover:text-yellow-700 transition-colors"
                            onClick={() => handleUpdateRecord(record.id)}
                            aria-label="Modifier le dossier"
                          >
                            <Pencil size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      Aucun dossier trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm text-gray-600 mb-4 md:mb-0">
              Affichage des dossiers {(currentPage - 1) * recordsPerPage + 1} à{" "}
              {Math.min(currentPage * recordsPerPage, (currentPage - 1) * recordsPerPage + filteredRecords.length)}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={firstPage}
                disabled={currentPage === 1}
                className={`p-1.5 rounded-md ${currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Première page"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`p-1.5 rounded-md ${currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Page précédente"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum =
                  totalPages <= 5
                    ? i + 1
                    : currentPage <= 3
                      ? i + 1
                      : currentPage >= totalPages - 2
                        ? totalPages - 4 + i
                        : currentPage - 2 + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => paginate(pageNum)}
                    className={`w-8 h-8 rounded-md text-sm ${currentPage === pageNum
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                      }`}
                    aria-label={`Page ${pageNum}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`p-1.5 rounded-md ${currentPage === totalPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Page suivante"
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={lastPage}
                disabled={currentPage === totalPages}
                className={`p-1.5 rounded-md ${currentPage === totalPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Dernière page"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecordForm;
